import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, Modal, message, Upload } from 'antd';
import type { UploadProps } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { RootState, AppDispatch } from '../../store';
import { setAuth, updateUser } from '../../store/slices/authSlice';
import { getCurrentUser, updateProfile } from '../../services/authService';
import { uploadCompanyLogo } from '../../services/fileService';
import AuthPageLayout from '../../components/auth/AuthPageLayout';
import AuthFormContainer, { Header } from '../../components/auth/AuthFormContainer';
import ButtonGroup from '../../components/common/ButtonGroup';
import IconFileCheck from '../../assets/icons/ic-file-check.svg';
import type { UpdateInternalUserProfileRequest, UpdateProcessOwnerProfileRequest } from '../../services/types';

const { Title, Text } = Typography;

const CompletionModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    padding: 24px;
  }

  .ant-modal-body {
    text-align: center;
    padding: 40px 24px 24px;
  }
`;

const ModalIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;

  img {
    width: 62px;
    height: 62px;
  }
`;

const ModalTitle = styled.h3`
  font-family: Inter;
  font-weight: 700;
  font-size: 20px;
  color: #1F2937;
  margin: 0 0 16px 0;
`;

const ModalDescription = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 16px;
  color: #6B7280;
  line-height: 1.6;
  margin: 0 0 32px 0;
`;

const LOGO_MAX_MB = 2;
const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp';

const LogoPreview = styled.div`
  margin-top: 8px;

  img {
    max-height: 80px;
    max-width: 200px;
    object-fit: contain;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
  }
`;

const OnboardingPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const isInternalAdmin = user?.userType === 'internalAdministrators';
  const isProcessOwner = user?.userType === 'processOwners';

  const getDescription = () => {
    if (isInternalAdmin) {
      return 'Please enter your company name to complete your profile setup.';
    }
    if (isProcessOwner) {
      return 'Please enter your job title and department to complete your profile setup.';
    }
    return 'Please complete your profile information to get started.';
  };

  useEffect(() => {
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const logoUploadProps: UploadProps = {
    accept: LOGO_ACCEPT,
    maxCount: 1,
    showUploadList: false,
    beforeUpload: (file) => {
      const allowed = ['image/png', 'image/jpeg', 'image/webp'];
      if (!allowed.includes(file.type)) {
        message.error('Only PNG, JPEG, or WebP images are allowed.');
        return Upload.LIST_IGNORE;
      }
      if (file.size / 1024 / 1024 >= LOGO_MAX_MB) {
        message.error(`Image must be smaller than ${LOGO_MAX_MB}MB.`);
        return Upload.LIST_IGNORE;
      }
      setLogoFile(file);
      setLogoPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });
      return false;
    },
  };

  const validateForm = () => {
    const values = form.getFieldsValue();

    if (isInternalAdmin) {
      setIsFormValid(!!values.companyName?.trim());
    } else if (isProcessOwner) {
      setIsFormValid(!!values.jobTitle?.trim() && !!values.department?.trim());
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      let payload: UpdateInternalUserProfileRequest | UpdateProcessOwnerProfileRequest;

      if (isInternalAdmin) {
        payload = {
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          phoneNumber: user?.phoneNumber || '',
          companyName: values.companyName,
        } as UpdateInternalUserProfileRequest;
      } else {
        payload = {
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          phoneNumber: user?.phoneNumber || '',
          jobTitle: values.jobTitle,
          department: values.department,
        } as UpdateProcessOwnerProfileRequest;
      }

      const response = await updateProfile(payload);

      // Update profile data but keep requiresOnboarding: true so the
      // routing guard doesn't redirect to dashboard before the modal shows.
      if (response.data) {
        dispatch(updateUser({ ...response.data, requiresOnboarding: true }));
      } else {
        dispatch(updateUser({ ...payload, requiresOnboarding: true }));
      }

      if (isInternalAdmin && logoFile && user?.companyId) {
        try {
          await uploadCompanyLogo(user.companyId, logoFile);
          const userResponse = await getCurrentUser();
          dispatch(setAuth({ user: userResponse.data }));
        } catch {
          message.warning('Profile saved, but company logo upload failed. You can add a logo later in Settings.');
        }
      }

      setShowCompletionModal(true);
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to update profile';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToDashboard = () => {
    dispatch(updateUser({ requiresOnboarding: false }));
    setShowCompletionModal(false);
    navigate('/dashboard', { replace: true });
  };

  return (
    <AuthPageLayout>
      <AuthFormContainer>
        <Header>
          <Title level={2}>Complete your profile</Title>
          <Text className="secondaryText">
            {getDescription()}
          </Text>
        </Header>

        <Form
          form={form}
          name="onboarding"
          layout="vertical"
          autoComplete="off"
          requiredMark={false}
          onValuesChange={validateForm}
        >
          {isInternalAdmin && (
            <>
              <Form.Item
                name="companyName"
                label="Company Name"
                rules={[{ required: true, message: 'Company name is required' }]}
              >
                <Input placeholder="Enter company name" />
              </Form.Item>
              <Form.Item label="Company Logo (optional)">
                <Upload {...logoUploadProps}>
                  <Button icon={<UploadOutlined />}>Upload logo (PNG, JPEG, WebP, max 2MB)</Button>
                </Upload>
                {logoPreview && (
                  <LogoPreview>
                    <img src={logoPreview} alt="Company logo preview" />
                  </LogoPreview>
                )}
              </Form.Item>
            </>
          )}

          {isProcessOwner && (
            <>
              <Form.Item
                name="jobTitle"
                label="Job Title"
                rules={[{ required: true, message: 'Job title is required' }]}
              >
                <Input placeholder="Enter job title" />
              </Form.Item>

              <Form.Item
                name="department"
                label="Department"
                rules={[{ required: true, message: 'Department is required' }]}
              >
                <Input placeholder="Enter department" />
              </Form.Item>
            </>
          )}
        </Form>

        <ButtonGroup>
          <Button
            type="primary"
            size="large"
            loading={loading}
            disabled={!isFormValid || loading}
            onClick={handleSubmit}
          >
            Complete Setup
          </Button>
        </ButtonGroup>
      </AuthFormContainer>

      <CompletionModal
        open={showCompletionModal}
        footer={null}
        centered
        closable={false}
        width={450}
        maskClosable={false}
      >
        <ModalIconWrapper>
          <img src={IconFileCheck} alt="Setup complete" />
        </ModalIconWrapper>
        <ModalTitle>You're all set!</ModalTitle>
        <ModalDescription>
          Welcome to OSA. Your profile has been set up successfully.
        </ModalDescription>
        <Button
          type="primary"
          size="large"
          block
          onClick={handleGoToDashboard}
        >
          Go to Dashboard
        </Button>
      </CompletionModal>
    </AuthPageLayout>
  );
};

export default OnboardingPage;
